import { MockDatabase, MockRecord, MockTable, TableQuery } from "../MockDatabase";

export type BundleOrganizationAttributionAPI = {
    attribution_guid: string;
    display_name: string;
    status: string;
    type: string;
    created_date: string;
    updated_date: string;
    organization_attribution_guid: string;
    required: boolean;
}

export class MockBundlesOrganizationsAttributons extends MockTable {
    constructor(db: MockDatabase) {
        super(db);
        this.name = 'bundles_organizations_attributions';
        this.fields = {
            id: this.PrimaryKey({
                fieldName: 'id'
            }),
            bundle_organization_attribution_guid: this.PublicKey({
                fieldName: 'bundle_organization_attribution_guid'
            }),
            organization_attribution_id: this.ForeignKey({
                fieldName: 'organization_attribution_id',
                nullable: false,
                index: true,
                foreignTableName: 'organizations_attributions',
                foreignTableFieldName: 'id',
            }),
            bundle_id: this.ForeignKey({
                fieldName: 'bundle_id',
                nullable: false,
                index: true,
                foreignTableName: 'bundles',
                foreignTableFieldName: 'id',
            }),
            deleted: this.Field({
                type: 'number',
                fieldName: 'deleted',
                nullable: false,
                default: {
                    value: 0
                }
            }),
            created_date: this.Field({
                type: 'datetime',
                fieldName: 'created_date',
                nullable: false,
                default: {
                    method: 'NOW'
                }
            }),
            updated_date: this.Field({
                type: 'datetime',
                fieldName: 'updated_date',
                nullable: false,
                default: {
                    method: 'NOW',
                    onUpdate: true
                }
            }),
        }
    }

    private _transformPayload(db: MockDatabase, values: Record<string, any>): Record<string, any> {
        let {
            organization_attribution_guid, organizationAttributionGuid, organization_attribution_id,
            bundle_guid, bundleGuid, bundle_id,
            ...data
        } = values;
        if ((organization_attribution_guid || organizationAttributionGuid) && !organization_attribution_id) {
            organization_attribution_id = db.resolveGuidToId('organizations_attributions', (organization_attribution_guid || organizationAttributionGuid));
        }
        if ((bundle_guid || bundleGuid) && !bundle_id) {
            bundle_id = db.resolveGuidToId('bundles', (bundle_guid || bundleGuid));
        }
        return {
            ...data,
            ...(bundle_id ? { bundle_id } : {}),
            ...(organization_attribution_id ? { organization_attribution_id } : {}),
        }
    }

    protected beforeQuery: (db: MockDatabase, table: MockTable, query: TableQuery) => TableQuery = (db, table, query) => {
        const transFormFields: Record<string, any> = {};
        const remQuery: TableQuery = [];
        query.forEach(entry => {
            if (entry.fieldName === 'organization_attribution_guid' || entry.fieldName === 'organizationAttributionGuid') {
                transFormFields['organization_attribution_guid'] = entry.value;
            } else if (entry.fieldName === 'bundle_guid' || entry.fieldName === 'bundleGuid') {
                transFormFields['bundle_guid'] = entry.value;
            } else {
                remQuery.push(entry);
            }
        });
        const fixed = this._transformPayload(db, transFormFields);
        Object.entries(fixed).forEach(([fieldName, value]) => remQuery.unshift({
            fieldName,
            value
        }));
        return remQuery;
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db, table, records, data) => {
        return this._transformPayload(db, data);
    }

    protected beforeCreate: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db, table, values) => {
        return this._transformPayload(db, values);
    }

    serialize = (record: MockRecord) => {
        const orgAttRecs = record.getRelated('organizations_attributions');
        const bundleRecs = record.getRelated('bundles');
        if (orgAttRecs.length !== 1 || bundleRecs.length !== 1) {
            throw new Error(`Data misconfiguration encountered while serializing ${this.name}. Invalid count of related records in tables "organizations" and "bundles"`);
        }
        const attributionRecs = orgAttRecs[0].getRelated('attributions');
        if (attributionRecs.length !== 1) {
            throw new Error(`Data misconfiguration encountered while serializing ${this.name}. Invalid count of related records in table "attributons"`);
        }
        return {
            attribution_guid: attributionRecs[0].values["attribution_guid"],
            display_name: attributionRecs[0].values["display_name"],
            status: attributionRecs[0].values["status"],
            type: attributionRecs[0].values["type"],
            created_date: record.values["created_date"],
            updated_date: record.values["updated_date"],
            organization_attribution_guid: orgAttRecs[0].values["organization_attribution_guid"],
            required: attributionRecs[0].values["required"],
        } as BundleOrganizationAttributionAPI;
    }
}